export const VCCertifcateTypes = Object.freeze({
  ARM_AMMUNITION_CERTIFICATE: "ARM_AMMUNITION_CERTIFICATE",
  DEFICIENCIES_DOCUMENTS: "DEFICIENCIES_DOCUMENTS",
  FLAG_STATE_PERMISSION: "FLAG_STATE_PERMISSION",
  LAST_PORT_CLEARANCE: "LAST_PORT_CLEARANCE",
} as const);

export type VCCertificateType = (typeof VCCertifcateTypes)[keyof typeof VCCertifcateTypes];

export const VRCertificateTypes = Object.freeze({
  REGISTRY_CERTIFICATE: "REGISTRY_CERTIFICATE",
  TONNAGE_CERTIFICATE: "TONNAGE_CERTIFICATE",
  LOAD_LINE_CERTIFICATE: "LOAD_LINE_CERTIFICATE",
  CARGO_SAFETY_CONSTRUCTION_CERTIFICATE: "CARGO_SAFETY_CONSTRUCTION_CERTIFICATE",
  CARGO_SAFETY_EQUIPMENT_CERTIFICATE: "CARGO_SAFETY_EQUIPMENT_CERTIFICATE",
  CARGO_SAFETY_RADIO_CERTIFICATE: "CARGO_SAFETY_RADIO_CERTIFICATE",
  SAFETY_MANAGEMENT_CERTIFICATE: "SAFETY_MANAGEMENT_CERTIFICATE",
  SHIP_SECURITY_CERTIFICATE: "SHIP_SECURITY_CERTIFICATE",
  OIL_POLLUTION_CERTIFICATE: "OIL_POLLUTION_CERTIFICATE",
  AIR_POLLUTION_CERTIFICATE: "AIR_POLLUTION_CERTIFICATE",
  SEWAGE_POLLUTION_CERTIFICATE: "SEWAGE_POLLUTION_CERTIFICATE",
  BALLAST_WATER_MANAGEMENT_CERTIFICATE: "BALLAST_WATER_MANAGEMENT_CERTIFICATE",
  CLASS_CERTIFICATE: "CLASS_CERTIFICATE",
  SANITATION_CONTROL_CERTIFICATE: "SANITATION_CONTROL_CERTIFICATE",
  NLS_CERTIFICATE: "NLS_CERTIFICATE",
  FITNESS_CERTIFICATE: "FITNESS_CERTIFICATE",
  OIL_POLLUTION_DAMAGE_CERTIFICATE: "OIL_POLLUTION_DAMAGE_CERTIFICATE",
  BUNKER_OIL_POLLUTION_CERTIFICATE: "BUNKER_OIL_POLLUTION_CERTIFICATE",
  WRECK_REMOVAL_CERTIFICATE: "WRECK_REMOVAL_CERTIFICATE",
  SEAFARER_REPATRIATION_CERTIFICATE: "SEAFARER_REPATRIATION_CERTIFICATE",
  SHIPOWNERS_LIABILITY_CERTIFICATE: "SHIPOWNERS_LIABILITY_CERTIFICATE",
  PASSENGER_SHIP_SAFETY_CERTIFICATE: "PASSENGER_SHIP_SAFETY_CERTIFICATE",
} as const);

export type VRCertificateType = (typeof VRCertificateTypes)[keyof typeof VRCertificateTypes];

export type Certificate = {
  type: VRCertificateType;
  required?: boolean;
};

export const VesselTypeCodes = Object.freeze({
  TANKER_SLOPE_BARGE: "TANKER(SLOPE BARGE)",
  TANKER_STRG_FACIL: "TANKER(STRG FACIL)",
  TANKER_BUNKR_BARGE: "TANKER(BUNKR BARGE)",
  TANKER: "TANKER",
  CHEMICAL_TANKER: "CHEMICALTANKER",
  LPG_CARRIER: "LPGCARRIER",
  LNG_CARRIER: "LNGCARRIER",
  PASSENGER_SHIP: "PASSENGERSHIP",
  RO_RO_VESSEL: "RO-ROVESSEL",
  LIVESTOCK_CARRIER: "LIVESTOCKCARRIER",
  OIL_BULK_ORE: "OIL/BULK ORE",
  CONTAINER: "CONTAINER",
  CAR_CARRIER: "CARCARRIER",
  REEFER: "REEFER",
  GENERAL_CARGO: "GENERALCARGO",
  HEAVY_LIFT_VSL: "HEAVYLIFTVSL",
  BULK_CARRIER: "BULKCARRIER",
  TUG: "TUG",
  WATER_BARGE: "WATERBARGE",
  LUBE_OIL_BARGE: "LUBEOILBARGE",
  BARGE: "BARGE",
  BARGE_SELF_PROPELLED: "BARGE (SELF-PROPELLED)",
  SKIMMER: "SKIMMER",
  CREW_BOAT: "CREWBOAT",
  DIVING_SUPPORT: "DIVINGSUPPORT",
  NAVY_VESSELS: "NAVYVESSELS",
  OTHERS: "OTHERS",
  SUPPLY_BOAT: "SUPPLYBOAT",
  RESEARCH_VESSEL: "RESEARCHVESSEL",
  CABLE_SHIP: "CABLESHIP",
  YACHT: "YACHT",
  OFFSHORE_PROJECT: "OFFSHORE PROJECT",
  WORK_BOAT: "WORKBOAT",
  FIREFIGHTING: "FIREFIGHTING",
  SPEED_BOAT: "SPEEDBOAT",
  LANDING_CRAFT: "LANDINGCRAFT",
  DREDGER: "DREDGER",
  DREDGER_SELF_PROPELLED: "DREDGER (SELF-PROPELLED)",
  DHOWS: "DHOWS",
} as const);

export type VesselTypeCode = (typeof VesselTypeCodes)[keyof typeof VesselTypeCodes];

export const CertificatesContexts = Object.freeze({
  BASE: "base",
  TERMINAL: "terminal",
  ANCHORAGE: "anchorage",
} as const);

export type CertificatesContext = (typeof CertificatesContexts)[keyof typeof CertificatesContexts];

const BaseCertificates = (grt?: number): Certificate[] => [
  { type: VRCertificateTypes.REGISTRY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.TONNAGE_CERTIFICATE, required: grt && grt >= 150 ? true : false },
];

const TERMINAL_BELOW_150_CERTIFICATES: Certificate[] = [
  { type: VRCertificateTypes.REGISTRY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.TONNAGE_CERTIFICATE },
  { type: VRCertificateTypes.LOAD_LINE_CERTIFICATE },
  { type: VRCertificateTypes.CLASS_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SANITATION_CONTROL_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SEAFARER_REPATRIATION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIPOWNERS_LIABILITY_CERTIFICATE, required: true },
];

const ANCHORAGE_BELOW_150_CERTIFICATES: Certificate[] = [
  { type: VRCertificateTypes.REGISTRY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.TONNAGE_CERTIFICATE },
  { type: VRCertificateTypes.SEAFARER_REPATRIATION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIPOWNERS_LIABILITY_CERTIFICATE, required: true },
];

const TERMINAL_GROUP_1: Certificate[] = [
  { type: VRCertificateTypes.REGISTRY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.TONNAGE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.LOAD_LINE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_CONSTRUCTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_EQUIPMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_RADIO_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SAFETY_MANAGEMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIP_SECURITY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.OIL_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.AIR_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SEWAGE_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.BALLAST_WATER_MANAGEMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CLASS_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SANITATION_CONTROL_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SEAFARER_REPATRIATION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIPOWNERS_LIABILITY_CERTIFICATE, required: true },
];

const TERMINAL_GROUP_1_TYPES: VesselTypeCode[] = [
  VesselTypeCodes.OTHERS,
  VesselTypeCodes.BULK_CARRIER,
  VesselTypeCodes.GENERAL_CARGO,
  VesselTypeCodes.CONTAINER,
  VesselTypeCodes.RO_RO_VESSEL,
  VesselTypeCodes.LIVESTOCK_CARRIER,
  VesselTypeCodes.CAR_CARRIER,
  VesselTypeCodes.REEFER,
  VesselTypeCodes.HEAVY_LIFT_VSL,
  VesselTypeCodes.WATER_BARGE,
  VesselTypeCodes.TUG,
  VesselTypeCodes.SKIMMER,
  VesselTypeCodes.CREW_BOAT,
  VesselTypeCodes.DIVING_SUPPORT,
  VesselTypeCodes.BARGE_SELF_PROPELLED,
  VesselTypeCodes.SUPPLY_BOAT,
  VesselTypeCodes.RESEARCH_VESSEL,
  VesselTypeCodes.CABLE_SHIP,
  VesselTypeCodes.YACHT,
  VesselTypeCodes.OFFSHORE_PROJECT,
  VesselTypeCodes.WORK_BOAT,
  VesselTypeCodes.FIREFIGHTING,
  VesselTypeCodes.SPEED_BOAT,
  VesselTypeCodes.LANDING_CRAFT,
  VesselTypeCodes.DREDGER_SELF_PROPELLED,
  VesselTypeCodes.DHOWS,
];

const TERMINAL_GROUP_2: Certificate[] = [
  { type: VRCertificateTypes.REGISTRY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.TONNAGE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.LOAD_LINE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_CONSTRUCTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_EQUIPMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_RADIO_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SAFETY_MANAGEMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIP_SECURITY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.OIL_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.AIR_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SEWAGE_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.BALLAST_WATER_MANAGEMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CLASS_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SANITATION_CONTROL_CERTIFICATE, required: true },
  { type: VRCertificateTypes.OIL_POLLUTION_DAMAGE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SEAFARER_REPATRIATION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIPOWNERS_LIABILITY_CERTIFICATE, required: true },
];

const TERMINAL_GROUP_2_TYPES: VesselTypeCode[] = [
  VesselTypeCodes.TANKER,
  VesselTypeCodes.TANKER_SLOPE_BARGE,
  VesselTypeCodes.TANKER_BUNKR_BARGE,
  VesselTypeCodes.TANKER_STRG_FACIL,
  VesselTypeCodes.LUBE_OIL_BARGE,
];

const TERMINAL_GROUP_3: Certificate[] = [
  { type: VRCertificateTypes.REGISTRY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.TONNAGE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.LOAD_LINE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_CONSTRUCTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_EQUIPMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_RADIO_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SAFETY_MANAGEMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIP_SECURITY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.OIL_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.AIR_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SEWAGE_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.BALLAST_WATER_MANAGEMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CLASS_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SANITATION_CONTROL_CERTIFICATE, required: true },
  { type: VRCertificateTypes.NLS_CERTIFICATE },
  { type: VRCertificateTypes.FITNESS_CERTIFICATE },
  { type: VRCertificateTypes.SEAFARER_REPATRIATION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIPOWNERS_LIABILITY_CERTIFICATE, required: true },
];

const TERMINAL_GROUP_3_TYPES: VesselTypeCode[] = [
  VesselTypeCodes.LNG_CARRIER,
  VesselTypeCodes.LPG_CARRIER,
];

const TERMINAL_GROUP_4: Certificate[] = [
  { type: VRCertificateTypes.REGISTRY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.TONNAGE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.LOAD_LINE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_CONSTRUCTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CLASS_CERTIFICATE, required: true },
];

const TERMINAL_GROUP_4_TYPES: VesselTypeCode[] = [VesselTypeCodes.BARGE, VesselTypeCodes.DREDGER];

const TERMINAL_GROUP_5: Certificate[] = [
  { type: VRCertificateTypes.REGISTRY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.TONNAGE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.LOAD_LINE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_CONSTRUCTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_EQUIPMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_RADIO_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SAFETY_MANAGEMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIP_SECURITY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.OIL_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.AIR_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SEWAGE_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.BALLAST_WATER_MANAGEMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CLASS_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SANITATION_CONTROL_CERTIFICATE, required: true },
  { type: VRCertificateTypes.NLS_CERTIFICATE },
  { type: VRCertificateTypes.FITNESS_CERTIFICATE },
  { type: VRCertificateTypes.OIL_POLLUTION_DAMAGE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SEAFARER_REPATRIATION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIPOWNERS_LIABILITY_CERTIFICATE, required: true },
];

const TERMINAL_GROUP_5_TYPES: VesselTypeCode[] = [VesselTypeCodes.CHEMICAL_TANKER];

const TERMINAL_GROUP_6: Certificate[] = [
  { type: VRCertificateTypes.REGISTRY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.TONNAGE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.LOAD_LINE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_CONSTRUCTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_EQUIPMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_RADIO_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SAFETY_MANAGEMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIP_SECURITY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.OIL_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.AIR_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SEWAGE_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.BALLAST_WATER_MANAGEMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CLASS_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SANITATION_CONTROL_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SEAFARER_REPATRIATION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIPOWNERS_LIABILITY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.PASSENGER_SHIP_SAFETY_CERTIFICATE, required: true },
];

const TERMINAL_GROUP_6_TYPES: VesselTypeCode[] = [VesselTypeCodes.PASSENGER_SHIP];

const TERMINAL_GROUP_7: Certificate[] = [
  { type: VRCertificateTypes.REGISTRY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.TONNAGE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.LOAD_LINE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_CONSTRUCTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_EQUIPMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CARGO_SAFETY_RADIO_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SAFETY_MANAGEMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIP_SECURITY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.OIL_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.AIR_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SEWAGE_POLLUTION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.BALLAST_WATER_MANAGEMENT_CERTIFICATE, required: true },
  { type: VRCertificateTypes.CLASS_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SANITATION_CONTROL_CERTIFICATE, required: true },
  { type: VRCertificateTypes.OIL_POLLUTION_DAMAGE_CERTIFICATE },
  { type: VRCertificateTypes.SEAFARER_REPATRIATION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIPOWNERS_LIABILITY_CERTIFICATE, required: true },
];

const TERMINAL_GROUP_7_TYPES: VesselTypeCode[] = [VesselTypeCodes.OIL_BULK_ORE];

const TERMINAL_GROUPS = Object.freeze({
  GROUP_1: { types: TERMINAL_GROUP_1_TYPES, certificates: TERMINAL_GROUP_1 },
  GROUP_2: { types: TERMINAL_GROUP_2_TYPES, certificates: TERMINAL_GROUP_2 },
  GROUP_3: { types: TERMINAL_GROUP_3_TYPES, certificates: TERMINAL_GROUP_3 },
  GROUP_4: { types: TERMINAL_GROUP_4_TYPES, certificates: TERMINAL_GROUP_4 },
  GROUP_5: { types: TERMINAL_GROUP_5_TYPES, certificates: TERMINAL_GROUP_5 },
  GROUP_6: { types: TERMINAL_GROUP_6_TYPES, certificates: TERMINAL_GROUP_6 },
  GROUP_7: { types: TERMINAL_GROUP_7_TYPES, certificates: TERMINAL_GROUP_7 },
} as const);

type TerminalGroupKey = keyof typeof TERMINAL_GROUPS;

const TERMINAL_VESSEL_GROUP: Partial<Record<VesselTypeCode, TerminalGroupKey>> = (() => {
  const map: Partial<Record<VesselTypeCode, TerminalGroupKey>> = {};

  (Object.keys(TERMINAL_GROUPS) as TerminalGroupKey[]).forEach((key) => {
    TERMINAL_GROUPS[key].types.forEach((t) => {
      map[t] = key;
    });
  });

  return map;
})();

const ANCH_GROUP_1: Certificate[] = [
  { type: VRCertificateTypes.REGISTRY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.TONNAGE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SEAFARER_REPATRIATION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIPOWNERS_LIABILITY_CERTIFICATE, required: true },
];

const ANCH_GROUP_1_TYPES: VesselTypeCode[] = [
  VesselTypeCodes.OTHERS,
  VesselTypeCodes.TANKER_BUNKR_BARGE,
  VesselTypeCodes.LNG_CARRIER,
  VesselTypeCodes.LPG_CARRIER,
  VesselTypeCodes.BULK_CARRIER,
  VesselTypeCodes.GENERAL_CARGO,
  VesselTypeCodes.CONTAINER,
  VesselTypeCodes.PASSENGER_SHIP,
  VesselTypeCodes.RO_RO_VESSEL,
  VesselTypeCodes.LIVESTOCK_CARRIER,
  VesselTypeCodes.CAR_CARRIER,
  VesselTypeCodes.REEFER,
  VesselTypeCodes.HEAVY_LIFT_VSL,
  VesselTypeCodes.WATER_BARGE,
  VesselTypeCodes.TUG,
  VesselTypeCodes.SKIMMER,
  VesselTypeCodes.CREW_BOAT,
  VesselTypeCodes.DIVING_SUPPORT,
  VesselTypeCodes.BARGE_SELF_PROPELLED,
  VesselTypeCodes.SUPPLY_BOAT,
  VesselTypeCodes.RESEARCH_VESSEL,
  VesselTypeCodes.CABLE_SHIP,
  VesselTypeCodes.YACHT,
  VesselTypeCodes.OFFSHORE_PROJECT,
  VesselTypeCodes.WORK_BOAT,
  VesselTypeCodes.FIREFIGHTING,
  VesselTypeCodes.SPEED_BOAT,
  VesselTypeCodes.LANDING_CRAFT,
  VesselTypeCodes.DREDGER_SELF_PROPELLED,
  VesselTypeCodes.DHOWS,
];

const ANCH_GROUP_2: Certificate[] = [
  { type: VRCertificateTypes.REGISTRY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.TONNAGE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.OIL_POLLUTION_DAMAGE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SEAFARER_REPATRIATION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIPOWNERS_LIABILITY_CERTIFICATE, required: true },
];

const ANCH_GROUP_2_TYPES: VesselTypeCode[] = [
  VesselTypeCodes.TANKER,
  VesselTypeCodes.TANKER_SLOPE_BARGE,
  VesselTypeCodes.TANKER_STRG_FACIL,
  VesselTypeCodes.CHEMICAL_TANKER,
  VesselTypeCodes.LUBE_OIL_BARGE,
];

const ANCH_GROUP_3: Certificate[] = [
  { type: VRCertificateTypes.REGISTRY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.TONNAGE_CERTIFICATE, required: true },
];

const ANCH_GROUP_3_TYPES: VesselTypeCode[] = [VesselTypeCodes.BARGE, VesselTypeCodes.DREDGER];

const ANCH_GROUP_4: Certificate[] = [
  { type: VRCertificateTypes.REGISTRY_CERTIFICATE, required: true },
  { type: VRCertificateTypes.TONNAGE_CERTIFICATE, required: true },
  { type: VRCertificateTypes.OIL_POLLUTION_DAMAGE_CERTIFICATE },
  { type: VRCertificateTypes.SEAFARER_REPATRIATION_CERTIFICATE, required: true },
  { type: VRCertificateTypes.SHIPOWNERS_LIABILITY_CERTIFICATE, required: true },
];

const ANCH_GROUP_4_TYPES: VesselTypeCode[] = [VesselTypeCodes.OIL_BULK_ORE];

const ANCHORAGE_GROUPS = Object.freeze({
  GROUP_1: { types: ANCH_GROUP_1_TYPES, certificates: ANCH_GROUP_1 },
  GROUP_2: { types: ANCH_GROUP_2_TYPES, certificates: ANCH_GROUP_2 },
  GROUP_3: { types: ANCH_GROUP_3_TYPES, certificates: ANCH_GROUP_3 },
  GROUP_4: { types: ANCH_GROUP_4_TYPES, certificates: ANCH_GROUP_4 },
} as const);

type AnchorageGroupKey = keyof typeof ANCHORAGE_GROUPS;

const ANCHORAGE_VESSEL_GROUP: Partial<Record<VesselTypeCode, AnchorageGroupKey>> = (() => {
  const map: Partial<Record<VesselTypeCode, AnchorageGroupKey>> = {};

  (Object.keys(ANCHORAGE_GROUPS) as AnchorageGroupKey[]).forEach((key) => {
    ANCHORAGE_GROUPS[key].types.forEach((t) => {
      map[t] = key;
    });
  });

  return map;
})();

const TERMINAL_WRECK_REMOVAL_TYPES: VesselTypeCode[] = [
  VesselTypeCodes.OTHERS,
  VesselTypeCodes.TANKER,
  VesselTypeCodes.TANKER_SLOPE_BARGE,
  VesselTypeCodes.TANKER_BUNKR_BARGE,
  VesselTypeCodes.TANKER_STRG_FACIL,
  VesselTypeCodes.CHEMICAL_TANKER,
  VesselTypeCodes.LNG_CARRIER,
  VesselTypeCodes.LPG_CARRIER,
  VesselTypeCodes.BULK_CARRIER,
  VesselTypeCodes.GENERAL_CARGO,
  VesselTypeCodes.CONTAINER,
  VesselTypeCodes.PASSENGER_SHIP,
  VesselTypeCodes.RO_RO_VESSEL,
  VesselTypeCodes.LIVESTOCK_CARRIER,
  VesselTypeCodes.OIL_BULK_ORE,
  VesselTypeCodes.CAR_CARRIER,
  VesselTypeCodes.REEFER,
  VesselTypeCodes.HEAVY_LIFT_VSL,
  VesselTypeCodes.LUBE_OIL_BARGE,
  VesselTypeCodes.WATER_BARGE,
  VesselTypeCodes.TUG,
  VesselTypeCodes.BARGE,
  VesselTypeCodes.SKIMMER,
  VesselTypeCodes.CREW_BOAT,
  VesselTypeCodes.DIVING_SUPPORT,
  VesselTypeCodes.BARGE_SELF_PROPELLED,
  VesselTypeCodes.SUPPLY_BOAT,
  VesselTypeCodes.RESEARCH_VESSEL,
  VesselTypeCodes.CABLE_SHIP,
  VesselTypeCodes.YACHT,
  VesselTypeCodes.OFFSHORE_PROJECT,
  VesselTypeCodes.WORK_BOAT,
  VesselTypeCodes.FIREFIGHTING,
  VesselTypeCodes.SPEED_BOAT,
  VesselTypeCodes.LANDING_CRAFT,
  VesselTypeCodes.DREDGER,
  VesselTypeCodes.DREDGER_SELF_PROPELLED,
  VesselTypeCodes.DHOWS,
];

const TERMINAL_BUNKER_OIL_POLLUTION_TYPES: VesselTypeCode[] = [
  VesselTypeCodes.TANKER,
  VesselTypeCodes.TANKER_SLOPE_BARGE,
  VesselTypeCodes.TANKER_BUNKR_BARGE,
  VesselTypeCodes.TANKER_STRG_FACIL,
  VesselTypeCodes.CHEMICAL_TANKER,
  VesselTypeCodes.LNG_CARRIER,
  VesselTypeCodes.LPG_CARRIER,
  VesselTypeCodes.BULK_CARRIER,
  VesselTypeCodes.GENERAL_CARGO,
  VesselTypeCodes.CONTAINER,
  VesselTypeCodes.PASSENGER_SHIP,
  VesselTypeCodes.RO_RO_VESSEL,
  VesselTypeCodes.LIVESTOCK_CARRIER,
  VesselTypeCodes.OIL_BULK_ORE,
  VesselTypeCodes.CAR_CARRIER,
  VesselTypeCodes.REEFER,
  VesselTypeCodes.HEAVY_LIFT_VSL,
  VesselTypeCodes.LUBE_OIL_BARGE,
  VesselTypeCodes.BARGE_SELF_PROPELLED,
  VesselTypeCodes.RESEARCH_VESSEL,
  VesselTypeCodes.CABLE_SHIP,
  VesselTypeCodes.OFFSHORE_PROJECT,
  VesselTypeCodes.LANDING_CRAFT,
  VesselTypeCodes.DREDGER_SELF_PROPELLED,
];

const ANCHORAGE_WRECK_REMOVAL_TYPES: VesselTypeCode[] = [
  VesselTypeCodes.OTHERS,
  VesselTypeCodes.TANKER,
  VesselTypeCodes.TANKER_SLOPE_BARGE,
  VesselTypeCodes.TANKER_BUNKR_BARGE,
  VesselTypeCodes.TANKER_STRG_FACIL,
  VesselTypeCodes.CHEMICAL_TANKER,
  VesselTypeCodes.LNG_CARRIER,
  VesselTypeCodes.LPG_CARRIER,
  VesselTypeCodes.BULK_CARRIER,
  VesselTypeCodes.GENERAL_CARGO,
  VesselTypeCodes.CONTAINER,
  VesselTypeCodes.PASSENGER_SHIP,
  VesselTypeCodes.RO_RO_VESSEL,
  VesselTypeCodes.LIVESTOCK_CARRIER,
  VesselTypeCodes.OIL_BULK_ORE,
  VesselTypeCodes.CAR_CARRIER,
  VesselTypeCodes.REEFER,
  VesselTypeCodes.HEAVY_LIFT_VSL,
  VesselTypeCodes.LUBE_OIL_BARGE,
  VesselTypeCodes.WATER_BARGE,
  VesselTypeCodes.TUG,
  VesselTypeCodes.BARGE,
  VesselTypeCodes.SKIMMER,
  VesselTypeCodes.CREW_BOAT,
  VesselTypeCodes.DIVING_SUPPORT,
  VesselTypeCodes.BARGE_SELF_PROPELLED,
  VesselTypeCodes.SUPPLY_BOAT,
  VesselTypeCodes.RESEARCH_VESSEL,
  VesselTypeCodes.CABLE_SHIP,
  VesselTypeCodes.YACHT,
  VesselTypeCodes.OFFSHORE_PROJECT,
  VesselTypeCodes.WORK_BOAT,
  VesselTypeCodes.FIREFIGHTING,
  VesselTypeCodes.SPEED_BOAT,
  VesselTypeCodes.LANDING_CRAFT,
  VesselTypeCodes.DREDGER,
  VesselTypeCodes.DREDGER_SELF_PROPELLED,
  VesselTypeCodes.DHOWS,
];

const ANCHORAGE_BUNKER_OIL_POLLUTION_TYPES: VesselTypeCode[] = [
  VesselTypeCodes.TANKER,
  VesselTypeCodes.TANKER_SLOPE_BARGE,
  VesselTypeCodes.TANKER_BUNKR_BARGE,
  VesselTypeCodes.TANKER_STRG_FACIL,
  VesselTypeCodes.CHEMICAL_TANKER,
  VesselTypeCodes.LNG_CARRIER,
  VesselTypeCodes.LPG_CARRIER,
  VesselTypeCodes.BULK_CARRIER,
  VesselTypeCodes.GENERAL_CARGO,
  VesselTypeCodes.CONTAINER,
  VesselTypeCodes.PASSENGER_SHIP,
  VesselTypeCodes.RO_RO_VESSEL,
  VesselTypeCodes.LIVESTOCK_CARRIER,
  VesselTypeCodes.OIL_BULK_ORE,
  VesselTypeCodes.CAR_CARRIER,
  VesselTypeCodes.REEFER,
  VesselTypeCodes.HEAVY_LIFT_VSL,
  VesselTypeCodes.LUBE_OIL_BARGE,
  VesselTypeCodes.BARGE_SELF_PROPELLED,
  VesselTypeCodes.RESEARCH_VESSEL,
  VesselTypeCodes.CABLE_SHIP,
  VesselTypeCodes.OFFSHORE_PROJECT,
  VesselTypeCodes.LANDING_CRAFT,
  VesselTypeCodes.DREDGER_SELF_PROPELLED,
];

const normalizeGrt = (grt?: number | string): number | undefined => {
  if (grt === null || grt === undefined || grt === "") return undefined;
  const n = Number(grt);
  return Number.isFinite(n) ? n : undefined;
};

const isBelow150 = (grt?: number): boolean => typeof grt === "number" && grt < 150;
const certificateTypeOrder = Object.values(VRCertificateTypes);

function sortCertificatesByTypeOrder(certificates: Certificate[]): Certificate[] {
  return [...certificates].sort(
    (a, b) => certificateTypeOrder.indexOf(a.type) - certificateTypeOrder.indexOf(b.type),
  );
}

function getTerminalListByVesselType(vesselType: VesselTypeCode, grt?: number): Certificate[] {
  if (vesselType === VesselTypeCodes.NAVY_VESSELS) return [];

  if (isBelow150(grt)) return [...TERMINAL_BELOW_150_CERTIFICATES];

  const groupKey = TERMINAL_VESSEL_GROUP[vesselType];

  return groupKey ? [...TERMINAL_GROUPS[groupKey].certificates] : [];
}

function getAnchorageListByVesselType(vesselType: VesselTypeCode, grt?: number): Certificate[] {
  if (vesselType === VesselTypeCodes.NAVY_VESSELS) return [];

  if (isBelow150(grt)) return [...ANCHORAGE_BELOW_150_CERTIFICATES];

  const groupKey = ANCHORAGE_VESSEL_GROUP[vesselType];

  return groupKey ? [...ANCHORAGE_GROUPS[groupKey].certificates] : [];
}

function applyConditionalCertificates(
  baseList: Certificate[],
  vesselType: VesselTypeCode,
  grt: number | undefined,
  opts: {
    wreckRemovalTypes: VesselTypeCode[];
    bunkerOilTypes: VesselTypeCode[];
  },
): Certificate[] {
  const list = [...baseList];
  const hasGrt = typeof grt === "number";

  if (hasGrt && grt > 300 && opts.wreckRemovalTypes.includes(vesselType)) {
    list.push({ type: VRCertificateTypes.WRECK_REMOVAL_CERTIFICATE, required: true });
  }

  if (hasGrt && grt > 1000 && opts.bunkerOilTypes.includes(vesselType)) {
    list.push({ type: VRCertificateTypes.BUNKER_OIL_POLLUTION_CERTIFICATE, required: true });
  }

  return list;
}

function getTerminalCertificates(vesselType: VesselTypeCode, grt?: number): Certificate[] {
  const baseList = getTerminalListByVesselType(vesselType, grt);
  return applyConditionalCertificates(baseList, vesselType, grt, {
    wreckRemovalTypes: TERMINAL_WRECK_REMOVAL_TYPES,
    bunkerOilTypes: TERMINAL_BUNKER_OIL_POLLUTION_TYPES,
  });
}

function getAnchorageCertificates(vesselType: VesselTypeCode, grt?: number): Certificate[] {
  const baseList = getAnchorageListByVesselType(vesselType, grt);
  return applyConditionalCertificates(baseList, vesselType, grt, {
    wreckRemovalTypes: ANCHORAGE_WRECK_REMOVAL_TYPES,
    bunkerOilTypes: ANCHORAGE_BUNKER_OIL_POLLUTION_TYPES,
  });
}

export function getVesselCertificates(
  context: CertificatesContext,
  vesselType: VesselTypeCode,
  grt?: number,
): Certificate[] {
  if (vesselType === VesselTypeCodes.NAVY_VESSELS) return [];

  const normalizedGrt = normalizeGrt(grt);

  if (context === CertificatesContexts.BASE) {
    const optional = new Map<VRCertificateType, Certificate>();

    for (const c of getTerminalCertificates(vesselType, normalizedGrt))
      optional.set(c.type, { type: c.type });
    for (const c of getAnchorageCertificates(vesselType, normalizedGrt))
      optional.set(c.type, { type: c.type });

    optional.delete(VRCertificateTypes.REGISTRY_CERTIFICATE);
    optional.delete(VRCertificateTypes.TONNAGE_CERTIFICATE);

    return sortCertificatesByTypeOrder([
      ...BaseCertificates(normalizedGrt),
      ...Array.from(optional.values()),
    ]);
  }

  if (context === CertificatesContexts.TERMINAL) {
    return sortCertificatesByTypeOrder(getTerminalCertificates(vesselType, normalizedGrt));
  }

  if (context === CertificatesContexts.ANCHORAGE) {
    return sortCertificatesByTypeOrder(getAnchorageCertificates(vesselType, normalizedGrt));
  }

  return [];
}

export const certificatesToClearWhenPIClubChanges = [
  VRCertificateTypes.OIL_POLLUTION_DAMAGE_CERTIFICATE,
  VRCertificateTypes.BUNKER_OIL_POLLUTION_CERTIFICATE,
  VRCertificateTypes.WRECK_REMOVAL_CERTIFICATE,
  VRCertificateTypes.SEAFARER_REPATRIATION_CERTIFICATE,
  VRCertificateTypes.SHIPOWNERS_LIABILITY_CERTIFICATE,
];

export const certificatesToClearWhenMLCPIClubChanges = [
  VRCertificateTypes.SEAFARER_REPATRIATION_CERTIFICATE,
  VRCertificateTypes.SHIPOWNERS_LIABILITY_CERTIFICATE,
];
